import React from 'react';
import { useResponsive } from '@sg/garnish';

import { type PaymentMethod } from '@order/graphql';
import { useSubmitPaymentMethod } from '@order/hooks';
import {
  BraintreeCreditCardForm,
  StripeCreditCardForm,
} from '@order/integrations';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTrackEventEffect } from '@order/Telemetry';

import { Modal } from '../Modal';

export const CreditCardAddModal = (props: CreditCardAddModalProps) => {
  const { visible, onRequestClose } = props;
  const onSuccess = props.onSuccess ?? onRequestClose;

  useTrackEventEffect({ name: 'payment_credit.add_card', skip: !visible });

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const { isSubmitting, submitPaymentMethod } = useSubmitPaymentMethod({
    onSuccess,
  });

  const shouldUseStripe = useFeatureFlag(
    'CELS-1108-use-stripe-for-new-payment-methods-enabled',
  );

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={t('credit-card-form.add')}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        {t('credit-card-form.add')}
      </Modal.Header>

      {shouldUseStripe ? (
        <StripeCreditCardForm
          isSubmitting={isSubmitting}
          handleOnSave={submitPaymentMethod}
        />
      ) : (
        <BraintreeCreditCardForm
          vault="order"
          isSubmitting={isSubmitting}
          handleOnSave={submitPaymentMethod}
        />
      )}
    </Modal>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardAddModalProps = Readonly<{
  visible: boolean;
  onRequestClose: () => void;

  /**
   * Optional callback to differentiate closing the modal vs submitting the form.
   * If not provided, {onRequestClose} will be used instead.
   */
  onSuccess?: (paymentMethod: PaymentMethod) => void;
}>;
